import type { GeocodeResult } from '../types/database';
import type { MapSettings } from './types';
import { fetchMapSettings } from './settings';
import { loadGoogleMaps } from './scriptLoader';
import { isGeocodeMockSydneyEnabled, mockSydneyGeocode } from './mockSydneyGeocode';
import { buildGoogleReverseGeocodeUrl } from './googleMapsBaseUrl';

async function geocodeWithGoogle(address: string, settings: MapSettings): Promise<GeocodeResult | null> {
  if (!settings.googleApiKey) return null;

  const maps = await loadGoogleMaps(settings.googleApiKey);
  const geocoder = new maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode(
      { address, componentRestrictions: { country: 'au' } },
      (results, status) => {
        const first = results?.[0];
        const location = first?.geometry?.location;
        if (status !== 'OK' || !first || !location) {
          resolve(null);
          return;
        }

        resolve({
          lat: location.lat(),
          lng: location.lng(),
          formatted_address: first.formatted_address,
        });
      }
    );
  });
}

/** Geocode in the browser using the Google Maps SDK (no edge function). */
export async function geocodeAddressClient(
  address: string,
  settings?: MapSettings
): Promise<GeocodeResult | null> {
  const trimmed = address.trim();
  if (!trimmed) return null;

  if (isGeocodeMockSydneyEnabled()) {
    return mockSydneyGeocode(trimmed);
  }

  const mapSettings = settings ?? (await fetchMapSettings());
  return geocodeWithGoogle(trimmed, mapSettings);
}

function preferredLanguage(): string {
  if (typeof navigator === 'undefined') return 'en';
  const lang = navigator.language || 'en';
  return lang.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

async function reverseGeocodeWithGoogleRest(
  lat: number,
  lng: number,
  apiKey: string,
  language: string
): Promise<string | null> {
  if (!apiKey) return null;
  try {
    const url = buildGoogleReverseGeocodeUrl(lat, lng, apiKey, language);
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      status?: string;
      results?: { formatted_address?: string }[];
    };
    if (data.status !== 'OK') return null;
    const formatted = data.results?.[0]?.formatted_address?.trim();
    return formatted || null;
  } catch {
    return null;
  }
}

async function reverseGeocodeWithGoogleJs(
  lat: number,
  lng: number,
  settings: MapSettings
): Promise<string | null> {
  if (!settings.googleApiKey) return null;
  try {
    const maps = await loadGoogleMaps(settings.googleApiKey);
    const geocoder = new maps.Geocoder();
    return await new Promise((resolve) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        const formatted = results?.[0]?.formatted_address?.trim();
        if (status !== 'OK' || !formatted) {
          resolve(null);
          return;
        }
        resolve(formatted);
      });
    });
  } catch {
    return null;
  }
}

/** Photon (Komoot) — street-level OpenStreetMap reverse geocode, no API key. */
async function reverseGeocodeWithPhoton(lat: number, lng: number, language: string): Promise<string | null> {
  try {
    const lang = language.startsWith('zh') ? 'en' : language;
    const url = `https://photon.komoot.io/reverse?lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lng))}&lang=${encodeURIComponent(lang)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      features?: {
        properties?: {
          name?: string;
          street?: string;
          housenumber?: string;
          district?: string;
          county?: string;
          city?: string;
          state?: string;
          country?: string;
        };
      }[];
    };
    const p = data.features?.[0]?.properties;
    if (!p) return null;
    const street = [p.housenumber, p.street || p.name].filter(Boolean).join(' ').trim();
    const parts = [street || p.name, p.district || p.county, p.city, p.state, p.country]
      .map((s) => s?.trim())
      .filter((s): s is string => Boolean(s));
    // de-dupe consecutive identical parts
    const unique: string[] = [];
    for (const part of parts) {
      if (unique[unique.length - 1] !== part) unique.push(part);
    }
    return unique.length > 0 ? unique.join(', ') : null;
  } catch {
    return null;
  }
}

/** BigDataCloud free client reverse geocode — good locality labels, supports zh. */
async function reverseGeocodeWithBigDataCloud(
  lat: number,
  lng: number,
  language: string
): Promise<string | null> {
  try {
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client` +
      `?latitude=${encodeURIComponent(String(lat))}` +
      `&longitude=${encodeURIComponent(String(lng))}` +
      `&localityLanguage=${encodeURIComponent(language)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      locality?: string;
      city?: string;
      principalSubdivision?: string;
      countryName?: string;
    };
    const parts = [data.locality, data.city, data.principalSubdivision, data.countryName]
      .map((s) => s?.trim())
      .filter((s): s is string => Boolean(s));
    const unique: string[] = [];
    for (const part of parts) {
      if (unique[unique.length - 1] !== part) unique.push(part);
    }
    return unique.length > 0 ? unique.join(', ') : null;
  } catch {
    return null;
  }
}

/**
 * Reverse-geocode lat/lng to a human-readable address.
 * Tries Google (REST then JS), then Photon, then BigDataCloud.
 * Does not use the Sydney forward-geocode mock — SOS / UI need a place name.
 */
export async function reverseGeocodeClient(
  lat: number,
  lng: number,
  settings?: MapSettings
): Promise<string | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const language = preferredLanguage();
  const mapSettings = settings ?? (await fetchMapSettings());

  const googleRest = await reverseGeocodeWithGoogleRest(lat, lng, mapSettings.googleApiKey, language);
  if (googleRest) return googleRest;

  const googleJs = await reverseGeocodeWithGoogleJs(lat, lng, mapSettings);
  if (googleJs) return googleJs;

  // Prefer locality-language for zh UI via BigDataCloud; Photon first for street names in en.
  if (language.startsWith('zh')) {
    const bdc = await reverseGeocodeWithBigDataCloud(lat, lng, 'zh');
    if (bdc) return bdc;
    const photon = await reverseGeocodeWithPhoton(lat, lng, language);
    if (photon) return photon;
  } else {
    const photon = await reverseGeocodeWithPhoton(lat, lng, language);
    if (photon) return photon;
    const bdc = await reverseGeocodeWithBigDataCloud(lat, lng, 'en');
    if (bdc) return bdc;
  }

  return null;
}
